import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Plus, Minus } from 'lucide-react';

// Mock data using real Premier League team logos
const mockGame = {
  homeTeam: 'Arsenal',
  awayTeam: 'Chelsea',
  homeTeamLogo: 'https://resources.premierleague.com/premierleague25/badges/3.svg',
  awayTeamLogo: 'https://resources.premierleague.com/premierleague25/badges/8.svg',
  matchDate: 'Sat, 15 Feb • 15:00'
};

export const PredictionDemo: React.FC = () => {
  const [homeScore, setHomeScore] = useState<number | null>(null);
  const [awayScore, setAwayScore] = useState<number | null>(null);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const sequence = [
      () => { setHomeScore(null); setAwayScore(null); setShowToast(false); },
      () => setHomeScore(2),
      () => setAwayScore(1),
      () => setShowToast(true),
      () => { /* pause */ },
    ];

    const timings = [0, 1000, 1800, 2600, 4500];
    
    const runSequence = () => {
      sequence.forEach((action, index) => {
        setTimeout(action, timings[index]);
      });
    };

    runSequence();
    const interval = setInterval(runSequence, 6000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col h-full pt-8 pb-4 px-2">
      {/* Header - Matching GamePredictionRow style */}
      <div className="text-center mb-3">
        <h3 className="text-white font-bold text-base">Gameweek 24</h3>
        <p className="text-white/60 text-[10px]">{mockGame.matchDate}</p>
      </div>

      {/* Match Card - Exact styling from GamePredictionRow */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/50 dark:border-gray-700/50 p-3 transition-all duration-200">
        {/* Match Date and Status */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-[9px] text-gray-600 dark:text-gray-400">
            {mockGame.matchDate}
          </div>
          <div className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
            Upcoming
          </div>
        </div>

        {/* Teams and Score */}
        <div className="flex items-center justify-between">
          {/* Home Team */}
          <div className="flex flex-col items-center space-y-1.5 flex-1 min-w-0">
            <img
              src={mockGame.homeTeamLogo}
              alt={`${mockGame.homeTeam} logo`}
              className="w-10 h-10 object-contain flex-shrink-0"
            />
            <div className="text-center">
              <div className="font-bold text-gray-900 dark:text-white text-[10px] leading-tight">
                {mockGame.homeTeam}
              </div>
            </div>
          </div>

          {/* Score Section */}
          <div className="flex flex-col items-center space-y-2 px-2 flex-shrink-0">
            <div className="flex items-center space-x-2">
              {/* Home Score Input */}
              <div className="flex flex-col items-center space-y-0.5">
                <div className="p-0.5 rounded bg-blue-100 dark:bg-blue-900/30">
                  <Plus className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
                </div>
                <motion.div
                  className="w-9 h-10 text-center text-base font-bold border-2 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center"
                  animate={{
                    borderColor: homeScore !== null ? 'rgb(37, 99, 235)' : 'rgb(147, 197, 253)',
                    backgroundColor: homeScore !== null ? 'rgb(239, 246, 255)' : 'rgb(249, 250, 251)'
                  }}
                >
                  <motion.span
                    className="text-gray-900 dark:text-white"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: homeScore !== null ? 1 : 0.3, scale: homeScore !== null ? 1 : 0.8 }}
                  >
                    {homeScore !== null ? homeScore : '–'}
                  </motion.span>
                </motion.div>
                <div className="p-0.5 rounded bg-blue-100 dark:bg-blue-900/30">
                  <Minus className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>

              <div className="text-base font-bold text-gray-400 dark:text-gray-500">V</div>

              {/* Away Score Input */}
              <div className="flex flex-col items-center space-y-0.5">
                <div className="p-0.5 rounded bg-blue-100 dark:bg-blue-900/30">
                  <Plus className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
                </div>
                <motion.div
                  className="w-9 h-10 text-center text-base font-bold border-2 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center"
                  animate={{
                    borderColor: awayScore !== null ? 'rgb(37, 99, 235)' : 'rgb(147, 197, 253)',
                    backgroundColor: awayScore !== null ? 'rgb(239, 246, 255)' : 'rgb(249, 250, 251)'
                  }}
                >
                  <motion.span
                    className="text-gray-900 dark:text-white"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: awayScore !== null ? 1 : 0.3, scale: awayScore !== null ? 1 : 0.8 }}
                  >
                    {awayScore !== null ? awayScore : '–'}
                  </motion.span>
                </motion.div>
                <div className="p-0.5 rounded bg-blue-100 dark:bg-blue-900/30">
                  <Minus className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Away Team */}
          <div className="flex flex-col items-center space-y-1.5 flex-1 min-w-0">
            <img
              src={mockGame.awayTeamLogo}
              alt={`${mockGame.awayTeam} logo`}
              className="w-10 h-10 object-contain flex-shrink-0"
            />
            <div className="text-center">
              <div className="font-bold text-gray-900 dark:text-white text-[10px] leading-tight">
                {mockGame.awayTeam}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification - Matching app toast styling */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            className="absolute bottom-16 left-3 right-3 bg-green-500 rounded-xl p-2.5 flex items-center gap-2 shadow-lg"
          >
            <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-green-500" />
            </div>
            <span className="text-white font-medium text-xs">Prediction Saved!</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feature label */}
      <div className="mt-auto pt-3 text-center">
        <span className="text-white/40 text-[10px] uppercase tracking-wider">Score Predictions</span>
      </div>
    </div>
  );
};